// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Mouse and trackpad, for the Free camera (M3-10, M3-31).
//
// The poses, the clamp and the orbit arithmetic have existed and been tested since M3-01. What
// they never had was anything to *move* them: `clampOrbit` and `orbitPosition` were called by
// their own tests and by nothing else, so Free was a static three-quarter shot and the word
// "orbit" in the requirement described an intention rather than a control.
//
// Split in two on purpose:
//
//   * **`OrbitDrag`** is the arithmetic — pixels to radians, wheel notches to metres — and has
//     no idea what a DOM event is. It is where every decision worth arguing about lives, and it
//     is testable without a browser.
//   * **`attachPointerOrbit`** is the listener plumbing, which is small, dull and impossible to
//     test headlessly, so there is as little of it as the job allows.
//
// Three details that are easy to leave out and unpleasant to leave out:
//
//   * **Pointer capture.** A player dragging fast will leave the canvas, and without capture the
//     orbit stops dead at the edge and then never gets its `pointerup`, so the next click drags
//     from the old anchor and the view leaps.
//   * **The wheel is not scroll.** Without `preventDefault` a trackpad pinch zooms the *page*
//     while it zooms the camera, and the whole harness slides about behind the machine.
//   * **Multiplicative zoom.** A wheel notch that adds a fixed number of metres is fine at two
//     metres and useless at fifty centimetres, where it crosses the whole usable range in one
//     notch.

import { clampOrbit, type Orbit } from '../world/cameras'

/**
 * Radians of azimuth for a drag across the full width of the canvas.
 *
 * A whole turn feels loose — the machine spins away from under the cursor — and half a turn
 * feels like the object is stuck to the pointer, which is the illusion worth having.
 */
export const AZIMUTH_PER_WIDTH = Math.PI

/**
 * Radians of elevation for a drag down the full height.
 *
 * Deliberately less than the azimuth rate against the same fraction of the screen: the
 * elevation clamp spans about 1.4 radians in total, so matching the rates would put the whole
 * usable range inside a third of a vertical drag and make it impossible to set precisely.
 */
export const ELEVATION_PER_HEIGHT = 1.6

/** Fraction of the current distance one wheel notch adds or removes. */
export const ZOOM_PER_NOTCH = 0.12

/** A wheel notch in `deltaMode: 0` pixels, which is what every mainstream browser reports. */
const PIXELS_PER_NOTCH = 100

export interface DragSurface {
  readonly width: number
  readonly height: number
}

/**
 * The orbit a drag and a wheel move, clamped after every change.
 *
 * Clamped on write rather than on read, so the stored state is always a legal orbit. Storing
 * an illegal one and clamping at the point of use produces a control that silently accumulates
 * — drag downward for a while past the floor and the camera then does nothing for the first
 * half of the drag back up, which reads as the mouse having missed.
 */
export class OrbitDrag {
  private state: Orbit
  private anchorX = 0
  private anchorY = 0
  private anchorAzimuth = 0
  private anchorElevation = 0
  private dragging = false

  /** Set false while any other camera preset is showing, so nothing accumulates unseen. */
  enabled = false

  constructor(initial: Orbit) {
    this.state = clampOrbit(initial.azimuth, initial.elevation, initial.distance)
  }

  get orbit(): Readonly<Orbit> {
    return this.state
  }

  get active(): boolean {
    return this.dragging
  }

  /**
   * Put the orbit somewhere, as when the Free camera is first entered from its authored pose.
   *
   * Ends any drag in progress. A reset mid-drag with the anchor left in place would apply the
   * rest of that drag as an offset from a position it no longer relates to.
   */
  reset(to: Orbit): void {
    this.state = clampOrbit(to.azimuth, to.elevation, to.distance)
    this.dragging = false
  }

  begin(x: number, y: number): void {
    if (!this.enabled) return
    this.dragging = true
    this.anchorX = x
    this.anchorY = y
    this.anchorAzimuth = this.state.azimuth
    this.anchorElevation = this.state.elevation
  }

  /**
   * Absolute rather than incremental: the angle is always the anchor plus the total travel.
   *
   * Accumulating per-move deltas instead loses a little at every clamp — drag into the floor,
   * drag back, and the view comes back to somewhere other than where it started, by however
   * much of the drag the clamp ate.
   */
  move(x: number, y: number, surface: DragSurface): void {
    if (!this.dragging || !this.enabled) return
    const width = Math.max(1, surface.width)
    const height = Math.max(1, surface.height)
    // Dragging right turns the machine to follow the pointer, which means the camera goes the
    // other way around it. Getting this backwards is not subtly wrong, it is inside-out.
    const azimuth = this.anchorAzimuth - ((x - this.anchorX) / width) * AZIMUTH_PER_WIDTH
    // Dragging *down* raises the camera, the same way dragging down on a map moves the map
    // down and the viewpoint up.
    const elevation = this.anchorElevation + ((y - this.anchorY) / height) * ELEVATION_PER_HEIGHT
    this.state = clampOrbit(azimuth, elevation, this.state.distance)
  }

  end(): void {
    this.dragging = false
  }

  /**
   * Wheel or trackpad pinch. `deltaY` is in pixels; positive is away from the player.
   *
   * Multiplicative, so a notch is the same *proportion* of the distance wherever you are. An
   * additive step tuned at two metres steps clean through the near clamp at fifty centimetres.
   */
  zoom(deltaY: number): void {
    if (!this.enabled || deltaY === 0) return
    const notches = deltaY / PIXELS_PER_NOTCH
    const distance = this.state.distance * Math.pow(1 + ZOOM_PER_NOTCH, notches)
    this.state = clampOrbit(this.state.azimuth, this.state.elevation, distance)
  }
}

/**
 * What the DOM side needs from an element, so a test can hand it something simpler.
 *
 * Listeners are typed as plain `Event` and narrowed inside, which is what an `HTMLCanvasElement`
 * actually satisfies. Declaring the narrow event type here instead makes the interface something
 * no real element is assignable to, and the fix for *that* is a cast at every call site.
 */
export interface OrbitTarget {
  addEventListener: (
    type: string,
    listener: (event: Event) => void,
    options?: boolean | AddEventListenerOptions,
  ) => void
  removeEventListener: (
    type: string,
    listener: (event: Event) => void,
    options?: boolean | EventListenerOptions,
  ) => void
  clientWidth: number
  clientHeight: number
  setPointerCapture?: (id: number) => void
  releasePointerCapture?: (id: number) => void
}

interface OrbitPointerEvent {
  pointerId: number
  clientX: number
  clientY: number
  button: number
  preventDefault: () => void
}

interface OrbitWheelEvent {
  deltaY: number
  deltaMode: number
  preventDefault: () => void
}

/** Lines per notch, for the browsers that report `deltaMode: 1`. */
const LINES_PER_NOTCH = 3

/**
 * Attach the drag to an element. Returns the detach.
 *
 * Only the primary button orbits: the secondary one is the context menu, and the middle one is
 * autoscroll on Windows, and taking either would be taking a control the player expects to work.
 */
export function attachPointerOrbit(element: OrbitTarget, drag: OrbitDrag): () => void {
  const surface = (): DragSurface => ({ width: element.clientWidth, height: element.clientHeight })

  const onDown = (event: Event): void => {
    const e = event as unknown as OrbitPointerEvent
    if (e.button !== 0 || !drag.enabled) return
    drag.begin(e.clientX, e.clientY)
    // Keep receiving moves after the pointer leaves the canvas. Without it a fast drag stops at
    // the edge and never sees its own `pointerup`, so the next click resumes from a stale anchor.
    element.setPointerCapture?.(e.pointerId)
  }

  const onMove = (event: Event): void => {
    if (!drag.active) return
    const e = event as unknown as OrbitPointerEvent
    drag.move(e.clientX, e.clientY, surface())
  }

  const onUp = (event: Event): void => {
    if (!drag.active) return
    drag.end()
    element.releasePointerCapture?.((event as unknown as OrbitPointerEvent).pointerId)
  }

  const onWheel = (event: Event): void => {
    if (!drag.enabled) return
    const e = event as unknown as OrbitWheelEvent
    // Or the page scrolls behind the machine while the camera zooms, and on a trackpad the
    // whole harness slides about under a pinch.
    e.preventDefault()
    drag.zoom(e.deltaMode === 1 ? e.deltaY * LINES_PER_NOTCH * 16 : e.deltaY)
  }

  element.addEventListener('pointerdown', onDown)
  element.addEventListener('pointermove', onMove)
  element.addEventListener('pointerup', onUp)
  // A pointer can be taken away rather than lifted — a system gesture, a lost device — and a
  // drag that never ends leaves the camera swinging behind a cursor nobody is holding down.
  element.addEventListener('pointercancel', onUp)
  element.addEventListener('wheel', onWheel, { passive: false })

  return () => {
    element.removeEventListener('pointerdown', onDown)
    element.removeEventListener('pointermove', onMove)
    element.removeEventListener('pointerup', onUp)
    element.removeEventListener('pointercancel', onUp)
    element.removeEventListener('wheel', onWheel)
  }
}
